import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Input variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */

/**
 * Edges of the two-line value. The input padding and the unit band share them,
 * so a unit stays on the number and clear of the field name.
 */
const twoLineValueLine = {
  input: 'pt-7 pb-1.5',
  affix: 'top-7 bottom-1.5 flex items-center',
} as const;
export const inputVariants = cva(
  [
    // Base styles - structural styles only
    'flex w-full',
    'h-11',
    'font-body',
    'rounded-field',
    'text-body text-b5',
    'px-xs py-xxs',
    'placeholder:text-subtext',
    'transition-colors duration-200',
    // File input styles
    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
    // Focus styles - visible focus ring per WCAG 2.1 AA
    // Override shadcn ring styles
    'focus-visible:outline-none',
    'focus-visible:ring-0',
    'focus-visible:ring-offset-0',
    // Disabled styles
    'disabled:cursor-not-allowed',
    'disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        default: [
          // Background and border colors
          'bg-surface-field',
          // Value text uses text-body (themeable) so it is not locked to purple
          'text-body',
          'border border-border-field',
          // Hover and focus read field-scoped tokens; hover follows the resting
          // border, so a borderless field stays borderless on hover.
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          // Disabled colors
          'disabled:bg-neutral-100',
          'disabled:border-neutral-200',
          'disabled:text-subtext',
        ],
        'two-line': [
          // Background and border colors - same as default
          'bg-surface-field',
          // Value text uses text-body to match the two-line Select
          'text-body',
          'border border-border-field',
          // Hover and focus read field-scoped tokens (see `default`).
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          // Disabled colors; the border is pinned so hover cannot show
          'disabled:bg-neutral-100',
          'disabled:border-border-field',
          'disabled:text-subtext',
          // Two-line specific: extra height for floating label
          'h-14',
          twoLineValueLine.input,
        ],
      },
      error: {
        true: [
          // Error border
          'border-error-500',
          'text-error-500',
          // Error hover and focus styles
          'hover:border-error-500',
          'focus-visible:border-error-500',
          'focus-visible:ring-2',
          'focus-visible:ring-error-100',
        ],
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'two-line',
        error: true,
        className: 'disabled:border-error-500',
      },
    ],
    defaultVariants: {
      variant: 'default',
      error: false,
    },
  },
);

export type InputVariantProps = VariantProps<typeof inputVariants>;

/** Vertical place of a unit or icon: centered on the default field, on the value line when two-line. */
export const inputAffixVariants = cva(
  'pointer-events-none absolute z-10 flex items-center',
  {
    variants: {
      variant: {
        default: 'top-1/2 -translate-y-1/2',
        'two-line': twoLineValueLine.affix,
      },
      side: {
        start: 'left-xs',
        end: 'right-xs',
        'end-with-control': 'right-2xl',
        beside: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      side: 'start',
    },
  },
);

/** Trailing control that reveals or conceals a password. Sits in the end-icon slot. */
export const inputPasswordToggleVariants = cva([
  'pointer-events-auto',
  'inline-flex h-lg w-lg shrink-0 items-center justify-center',
  'rounded-sm text-black',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-primary-600',
  'focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed',
  'disabled:opacity-60',
]);

/**
 * Keep the browser number spinner visible. WebKit otherwise shows it only on hover.
 * Hide it when step is "any", or an end icon would sit on top of it.
 * With an end unit, pin the spinner to the field edge. The unit stays beside
 * the value and only stops before the spinner when the value fills the field.
 */
export const inputSpinnerVariants = cva('', {
  variants: {
    show: {
      true: [
        '[&::-webkit-inner-spin-button]:opacity-100',
        '[&::-webkit-outer-spin-button]:opacity-100',
      ].join(' '),
      false: [
        '[appearance:textfield]',
        '[&::-webkit-inner-spin-button]:m-0',
        '[&::-webkit-inner-spin-button]:appearance-none',
        '[&::-webkit-outer-spin-button]:m-0',
        '[&::-webkit-outer-spin-button]:appearance-none',
      ].join(' '),
    },
    afterUnit: {
      true: [
        '[&::-webkit-inner-spin-button]:absolute',
        '[&::-webkit-inner-spin-button]:right-xs',
        '[&::-webkit-inner-spin-button]:m-0',
        '[&::-webkit-inner-spin-button]:opacity-100',
      ].join(' '),
      false: '',
    },
    variant: {
      default: '',
      'two-line': '',
    },
  },
  compoundVariants: [
    {
      show: true,
      afterUnit: true,
      variant: 'default',
      class: [
        '[&::-webkit-inner-spin-button]:top-1/2',
        '[&::-webkit-inner-spin-button]:h-lg',
        '[&::-webkit-inner-spin-button]:-translate-y-1/2',
      ].join(' '),
    },
    {
      show: true,
      afterUnit: true,
      variant: 'two-line',
      class: [
        '[&::-webkit-inner-spin-button]:top-7',
        '[&::-webkit-inner-spin-button]:bottom-1.5',
        '[&::-webkit-inner-spin-button]:h-auto',
      ].join(' '),
    },
  ],
  defaultVariants: {
    show: true,
    afterUnit: false,
    variant: 'default',
  },
});
